import React from 'react';

const LIFETIME_STEPS = [2200, 1900, 1650, 1450, 1300, 1150];
const STEP_SIZE = 10;
const GAME_DURATION_MS = 60_000;
const TARGET_RADIUS_MOBILE = 26;
const TARGET_RADIUS_DESKTOP = 26;

function getLifetimeForTap(tapCount) {
  const idx = Math.min(Math.floor(tapCount / STEP_SIZE), LIFETIME_STEPS.length - 1);
  return LIFETIME_STEPS[idx];
}

function getTargetRadius() {
  return window.innerWidth < 640 ? TARGET_RADIUS_MOBILE : TARGET_RADIUS_DESKTOP;
}

function getDailySeed() {
  const d = new Date();
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

function seededRng(seed) {
  let s = seed;
  return () => {
    s |= 0;
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randomPosition(rng, areaW, areaH, radius) {
  const pad    = 14;
  const arcR   = radius + 6;
  const margin = arcR + pad + 4;
  return {
    x: margin + rng() * (areaW - margin * 2),
    y: margin + rng() * (areaH - margin * 2),
  };
}

// ─── Target ───────────────────────────────────────────────────────────────────
function Target({ x, y, lifetime, onTap, radius }) {
  const [elapsed, setElapsed] = React.useState(0);
  const [tapped, setTapped]   = React.useState(false);
  const startRef = React.useRef(Date.now());

  React.useEffect(() => {
    let raf;
    const tick = () => {
      setElapsed(Date.now() - startRef.current);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const progress = Math.min(elapsed / lifetime, 1);

  // colour: purple(270) → orange(30) as timer runs out
  const hue        = Math.round(270 - progress * 240);
  const urgency    = progress > 0.7;            // last 30 % — start shaking
  const nearDeath  = progress > 0.88;           // last 12 % — intense pulse

  const pulseR      = radius - 2;          // sits just outside the arc
  const pulseOpacity = Math.max(0, 0.4 - progress * 0.38);

  // countdown arc — sits outside the filled ball with a clear gap
  const arcR         = radius + 6;
  const circumference = 2 * Math.PI * arcR;
  const dashOffset   = circumference * progress;

  // inner fill — smaller than arcR so arc never overlaps ball
  const fillR = radius - 2;

  // SVG canvas — large enough for arc + stroke without clipping
  const pad  = 14;                         // half strokeWidth(4) + glow bleed
  const size = (arcR + pad) * 2;
  const cx   = size / 2;
  const cy   = size / 2;

  const handlePointer = (e) => {
    e.preventDefault();
    if (tapped) return;
    setTapped(true);
    onTap(Date.now() - startRef.current);
  };

  return (
    <div
      onPointerDown={handlePointer}
      style={{
        position: 'absolute',
        left: x - size / 2,
        top:  y - size / 2,
        width: size,
        height: size,
        cursor: 'pointer',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        // pop-in on mount
        animation: 'targetSpawn 0.18s cubic-bezier(0.34,1.56,0.64,1) both',
      }}
    >
      <style>{`
        @keyframes targetSpawn {
          from { transform: scale(0.3); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }
        @keyframes urgentShake {
          0%,100% { transform: translate(0,0) scale(1); }
          20%     { transform: translate(-2px, 1px) scale(1.04); }
          40%     { transform: translate(2px,-1px) scale(0.97); }
          60%     { transform: translate(-1px, 2px) scale(1.02); }
          80%     { transform: translate(1px,-2px) scale(0.98); }
        }
        @keyframes innerBeat {
          0%,100% { transform: scale(1); }
          50%     { transform: scale(1.08); }
        }
      `}</style>

      <svg
        width={size} height={size}
        style={{
          overflow: 'hidden',
          animation: nearDeath
            ? 'urgentShake 0.25s ease-in-out infinite'
            : urgency
            ? 'urgentShake 0.45s ease-in-out infinite'
            : 'none',
        }}
      >
        <defs>
          <radialGradient id={`tg-${startRef.current}`} cx="40%" cy="35%">
            <stop offset="0%"   stopColor={`hsl(${hue},90%,80%)`} />
            <stop offset="100%" stopColor={`hsl(${hue},75%,52%)`} />
          </radialGradient>
          <filter id={`tglow-${startRef.current}`} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation={nearDeath ? '5' : '3'} result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* outer pulse ring — SVG-native animate so it expands from its own centre */}
        {pulseOpacity > 0 && (
          <circle cx={cx} cy={cy} r={pulseR} fill="none" stroke={`hsl(${hue},80%,60%)`} strokeWidth="1.5">
            <animate attributeName="r"       from={pulseR}      to={pulseR + 10}  dur="1.1s" repeatCount="indefinite" />
            <animate attributeName="opacity" from={pulseOpacity} to="0"           dur="1.1s" repeatCount="indefinite" />
          </circle>
        )}

        {/* countdown arc track (faint) */}
        <circle
          cx={cx} cy={cy} r={arcR}
          fill="none"
          stroke={`hsla(${hue},60%,70%,0.22)`}
          strokeWidth="4"
        />

        {/* countdown arc — drains as time runs out */}
        <circle
          cx={cx} cy={cy} r={arcR}
          fill="none"
          stroke={`hsl(${hue},85%,55%)`}
          strokeWidth="4"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          filter={`url(#tglow-${startRef.current})`}
        />

        {/* filled inner circle */}
        <circle
          cx={cx} cy={cy} r={fillR}
          fill={`url(#tg-${startRef.current})`}
          filter={`url(#tglow-${startRef.current})`}
          style={{
            animation: nearDeath
              ? 'innerBeat 0.3s ease-in-out infinite'
              : urgency
              ? 'innerBeat 0.55s ease-in-out infinite'
              : 'innerBeat 1.4s ease-in-out infinite',
          }}
        />

        {/* TAP label */}
        <text
          x={cx} y={cy + radius * 0.14}
          textAnchor="middle"
          fontSize={radius * 0.36}
          fontWeight="900"
          letterSpacing="1"
          fill="white"
          style={{ pointerEvents: 'none', textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
        >
          TAP
        </text>

        {/* small dot in centre for precision */}
        <circle cx={cx} cy={cy} r="2.5" fill="rgba(255,255,255,0.7)" style={{ pointerEvents: 'none' }} />
      </svg>
    </div>
  );
}

// ─── ScoreBoard ───────────────────────────────────────────────────────────────
function ScoreBoard({ score, combo, timeLeft, totalTaps }) {
  const secs = Math.max(0, Math.ceil(timeLeft / 1000));
  const urgent = secs <= 10;
  return (
    <div className="flex items-center justify-center gap-2 px-4 py-2 bg-white border-b border-slate-200 shadow-sm">
      <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-2">
        <div className="text-center">
          <div className="text-base font-extrabold text-slate-900 tabular-nums leading-tight">{score}</div>
          <div className="text-[9px] text-purple-500 uppercase tracking-wide font-semibold">Score</div>
        </div>
        <div className="w-px h-8 bg-slate-200" />
        <div className="text-center">
          <div className={`text-base font-extrabold tabular-nums leading-tight ${combo > 1 ? 'text-orange-500' : 'text-slate-400'}`}>
            {combo > 1 ? `×${combo}` : '×1'}
          </div>
          <div className="text-[9px] text-purple-500 uppercase tracking-wide font-semibold">Combo</div>
        </div>
        <div className="w-px h-8 bg-slate-200" />
        <div className="text-center">
          <div className={`font-mono text-base font-extrabold tabular-nums leading-tight ${urgent ? 'text-red-500 animate-pulse' : 'text-slate-900'}`}>
            {secs}s
          </div>
          <div className="text-[9px] text-purple-500 uppercase tracking-wide font-semibold">Time</div>
        </div>
        <div className="w-px h-8 bg-slate-200" />
        <div className="text-center">
          <div className="text-base font-extrabold text-slate-900 tabular-nums leading-tight">{totalTaps}</div>
          <div className="text-[9px] text-purple-500 uppercase tracking-wide font-semibold">Taps</div>
        </div>
      </div>
    </div>
  );
}

// ─── GameOverModal ────────────────────────────────────────────────────────────
function GameOverModal({ stats, isNewBest, onSubmit, submitting }) {
  const avgMs = stats.reactionTimes.length
    ? Math.round(stats.reactionTimes.reduce((a, b) => a + b, 0) / stats.reactionTimes.length)
    : 0;

  const statItems = [
    { label: 'Score',      value: stats.score,               accent: 'text-purple-600', bg: 'bg-purple-50 border-purple-100' },
    { label: 'Taps',       value: stats.totalTaps,           accent: 'text-orange-500', bg: 'bg-orange-50 border-orange-100' },
    { label: 'Best Combo', value: `x${stats.maxCombo}`,      accent: 'text-rose-500',   bg: 'bg-rose-50 border-rose-100'     },
    { label: 'Avg React',  value: avgMs ? `${avgMs}ms` : '-', accent: 'text-amber-600', bg: 'bg-amber-50 border-amber-100'   },
  ];

  return (
    <div
      className="absolute inset-0 z-10 flex flex-col justify-end"
      style={{ background: 'rgba(248,250,252,0.55)', backdropFilter: 'blur(3px)' }}
    >
      {/* big score in the frosted upper area */}
      <div className="flex-1 flex flex-col items-center justify-center gap-2">
        <div className="text-6xl font-black tabular-nums bg-gradient-to-br from-purple-600 to-orange-500 bg-clip-text text-transparent">
          {stats.score}
        </div>
        <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Final Score</div>
        {isNewBest && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-300 px-3 py-1 text-xs font-bold text-amber-600">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z"/></svg>
            New Personal Best!
          </span>
        )}
      </div>

      {/* bottom sheet */}
      <div className="rounded-t-3xl border-t border-slate-200 bg-white px-5 pt-4 pb-6 shadow-2xl">
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-slate-200" />
        <div className="mb-1 text-center text-lg font-extrabold text-slate-900">Time's Up!</div>
        <div className="mb-4 text-center text-xs text-slate-400">Here's how you did today</div>

        <div className="grid grid-cols-4 gap-2 mb-5">
          {statItems.map(s => (
            <div key={s.label} className={`rounded-2xl border p-2.5 text-center ${s.bg}`}>
              <div className={`text-sm font-extrabold tabular-nums ${s.accent}`}>{s.value}</div>
              <div className="text-[9px] text-slate-400 font-semibold leading-tight mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <button
          onClick={onSubmit}
          disabled={submitting}
          className="w-full rounded-2xl bg-gradient-to-r from-purple-600 to-orange-500 py-3.5 text-sm font-bold text-white shadow-lg shadow-purple-200 active:scale-95 transition disabled:opacity-60"
        >
          {submitting
            ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
                Submitting...
              </span>
            )
            : 'Submit & View Leaderboard'
          }
        </button>
      </div>
    </div>
  );
}

// ─── Ready Screen ─────────────────────────────────────────────────────────────
function ReadyScreen({ onStart }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-5 text-center overflow-y-auto">
      <div className="flex h-16 w-16 sm:h-20 sm:w-20 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-orange-500 shadow-2xl shadow-purple-200">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
      </div>
      <div>
        <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">Reflex Tap</h2>
        <p className="mt-1.5 text-xs sm:text-sm text-slate-500 max-w-[260px] mx-auto leading-5">
          Tap targets before they vanish. Combos multiply your score. Speed increases gradually!
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2 w-full max-w-[260px]">
        {[
          { label: 'Duration',  value: '60s' },
          { label: 'Combo',     value: '×taps' },
          { label: 'Starts at', value: `${LIFETIME_STEPS[0]}ms` },
          { label: 'Fastest',   value: `${LIFETIME_STEPS[LIFETIME_STEPS.length - 1]}ms` },
        ].map(s => (
          <div key={s.label} className="rounded-xl bg-slate-50 border border-slate-200 px-2 py-2 text-center">
            <div className="text-sm font-bold text-slate-900">{s.value}</div>
            <div className="text-[10px] text-purple-500">{s.label}</div>
          </div>
        ))}
      </div>
      <button
        onClick={onStart}
        className="w-full max-w-[260px] rounded-xl bg-gradient-to-r from-purple-600 to-orange-500 py-3.5 text-sm font-bold text-white shadow-lg shadow-purple-200 active:scale-95 transition"
      >
        Start Game
      </button>
    </div>
  );
}

// ─── Main Game ────────────────────────────────────────────────────────────────
export default function ReflexTapGame({ onComplete, onBack, personalBestScore }) {
  const areaRef = React.useRef(null);

  const g = React.useRef({
    phase: 'ready',
    score: 0,
    combo: 1,
    maxCombo: 1,
    taps: 0,
    tapCount: 0,
    reactionTimes: [],
    timeLeft: GAME_DURATION_MS,
    rng: seededRng(getDailySeed()),
    radius: getTargetRadius(),
    missTimer: null,
    gameTimer: null,
  });

  const [phase, setPhase]           = React.useState('ready');
  const [target, setTarget]         = React.useState(null);
  const [timeLeft, setTimeLeft]     = React.useState(GAME_DURATION_MS);
  const [score, setScore]           = React.useState(0);
  const [combo, setCombo]           = React.useState(1);
  const [totalTaps, setTotalTaps]   = React.useState(0);
  const [finalStats, setFinalStats] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [radius, setRadius]         = React.useState(getTargetRadius);

  React.useEffect(() => {
    const onResize = () => {
      const r = getTargetRadius();
      g.current.radius = r;
      setRadius(r);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  React.useEffect(() => {
    return () => {
      clearTimeout(g.current.missTimer);
      clearInterval(g.current.gameTimer);
      g.current.visibilityCleanup?.();
    };
  }, []);

  function spawnTarget() {
    if (g.current.phase !== 'playing') return;
    const area = areaRef.current;
    if (!area) return;

    const { width, height } = area.getBoundingClientRect();
    const r = g.current.radius;
    const pos = randomPosition(g.current.rng, width, height, r);
    const lifetime = getLifetimeForTap(g.current.tapCount);

    clearTimeout(g.current.missTimer);
    setTarget({ ...pos, lifetime, key: Date.now() });

    g.current.missTimer = setTimeout(() => {
      if (g.current.phase !== 'playing') return;
      g.current.combo = 1;
      setCombo(1);
      spawnTarget();
    }, lifetime);
  }

  function endGame() {
    if (g.current.phase === 'over') return;
    g.current.phase = 'over';
    clearTimeout(g.current.missTimer);
    clearInterval(g.current.gameTimer);
    g.current.visibilityCleanup?.();
    g.current.visibilityCleanup = null;
    setPhase('over');
    setTarget(null);

    const times = g.current.reactionTimes;
    const avgReactionTime = times.length
      ? Math.round(times.reduce((a, b) => a + b, 0) / times.length)
      : 0;

    setFinalStats({
      score: g.current.score,
      totalTaps: g.current.taps,
      maxCombo: g.current.maxCombo,
      reactionTimes: [...times],
      averageReactionTime: avgReactionTime,
      completionTime: GAME_DURATION_MS,
    });
  }

  function startGame() {
    g.current.phase = 'playing';
    g.current.score = 0;
    g.current.combo = 1;
    g.current.maxCombo = 1;
    g.current.taps = 0;
    g.current.tapCount = 0;
    g.current.reactionTimes = [];
    g.current.timeLeft = GAME_DURATION_MS;
    g.current.rng = seededRng(getDailySeed());

    setPhase('playing');
    setScore(0);
    setCombo(1);
    setTotalTaps(0);
    setTimeLeft(GAME_DURATION_MS);
    setFinalStats(null);

    const startedAt = Date.now();

    const tick = () => {
      if (g.current.phase !== 'playing') return;
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, GAME_DURATION_MS - elapsed);
      g.current.timeLeft = remaining;
      setTimeLeft(remaining);
      if (remaining <= 0) endGame();
    };

    // setInterval ensures tick fires even when tab is backgrounded/throttled
    g.current.gameTimer = setInterval(tick, 100);

    // visibilitychange: force an immediate tick when tab comes back into focus
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    g.current.visibilityCleanup = () => document.removeEventListener('visibilitychange', onVisible);

    spawnTarget();
  }

  function handleTap(reactionMs) {
    if (g.current.phase !== 'playing') return;
    clearTimeout(g.current.missTimer);

    const newCombo = g.current.combo + 1;
    g.current.combo = newCombo;
    if (newCombo > g.current.maxCombo) g.current.maxCombo = newCombo;

    g.current.score += 10 * newCombo;
    g.current.taps += 1;
    g.current.tapCount += 1;
    g.current.reactionTimes.push(reactionMs);

    setScore(g.current.score);
    setCombo(newCombo);
    setTotalTaps(g.current.taps);

    spawnTarget();
  }

  const handleSubmit = React.useCallback(() => {
    if (!finalStats || submitting) return;
    setSubmitting(true);
    onComplete(finalStats);
  }, [finalStats, submitting, onComplete]);

  const isNewBest = finalStats != null && (personalBestScore == null || finalStats.score > personalBestScore);

  return (
    <div
      className="flex flex-col select-none"
      style={{
        height: '100dvh',
        background: 'radial-gradient(circle at 10% 0%, rgba(124,58,237,0.08), transparent 35%), radial-gradient(circle at 90% 5%, rgba(249,115,22,0.07), transparent 30%), #f8fafc',
        touchAction: 'none',
        overscrollBehavior: 'none',
      }}
    >
      {/* Top bar */}
      <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
        <button
          onClick={onBack}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 active:scale-95 transition shadow-sm"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
        </button>
        <span className="text-sm font-extrabold text-slate-900">Reflex Tap</span>
        <span className="rounded-full bg-gradient-to-r from-purple-100 to-orange-100 border border-purple-200 px-2 py-0.5 text-[10px] font-bold text-purple-600">Daily</span>
      </div>

      {/* ScoreBoard */}
      {phase === 'playing' && (
        <div className="shrink-0">
          <ScoreBoard score={score} combo={combo} timeLeft={timeLeft} totalTaps={totalTaps} />
        </div>
      )}

      {/* Game area */}
      <div
        className="flex-1 flex justify-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.05) 0%, rgba(249,115,22,0.04) 100%)' }}
      >
        <div
          ref={areaRef}
          className="relative w-full max-w-lg overflow-hidden border-x border-slate-200/60"
          style={{
            touchAction: 'none',
            background: 'linear-gradient(135deg, rgba(124,58,237,0.15) 0%, rgba(249,115,22,0.13) 100%)',
          }}
        >
          {phase === 'ready' && <ReadyScreen onStart={startGame} />}

          {phase === 'playing' && target && (
            <Target
              key={target.key}
              x={target.x}
              y={target.y}
              lifetime={target.lifetime}
              onTap={handleTap}
              radius={radius}
            />
          )}

          {phase === 'over' && finalStats && (
            <GameOverModal
              stats={finalStats}
              isNewBest={isNewBest}
              onSubmit={handleSubmit}
              submitting={submitting}
            />
          )}
        </div>
      </div>
    </div>
  );
}
